// src/pages/Travel.tsx — Travel, Accommodation & Fatigue Management module
// Regulatory basis: WHS Act 2011 (QLD) · Safe Work Australia Code of Practice (Sep 2025)
// ENHANCED: Full booking modal, bed capacity, multi-employee assignment, mobile push notification
import { useState, useMemo } from 'react'
import { useAuthStore } from '../stores/auth.store'
import {
  MOCK_FM_FLAGS, MOCK_ACCOMMODATION, MOCK_DISTANCE_MATRIX, MOCK_AWAY_REGISTER,
  EMPLOYEE_FM_PROFILES, FM_RISK_META, FM_STATUS_META, ACCOMMODATION_STATUS_META,
  DIST_STATUS_META, SITES, FM_THRESHOLDS,
} from '../utils/mockDataTravel'
import { MOCK_EMPLOYEES } from '../utils/mockDataExtended'
import { MOCK_JOBS } from '../utils/mockData'
import { MOCK_ALLOCATIONS, WEEK_DAYS } from '../utils/mockDataRoster'
import { formatCurrency, formatDate } from '../utils/formatters'
import {
  MapPin, Shield, CheckCircle, AlertTriangle, X, Clock, Users,
  ChevronDown, ChevronUp, Plus, Activity, Bed, Phone, Mail,
  Send, Key, Home, Smartphone, RefreshCw, UserPlus, UserMinus,
  RotateCcw, History, AlertCircle, Info, Receipt, DollarSign,
} from 'lucide-react'

// ─────────────────────────────────────────────────────────────────
// EXPENSE CLAIMS — Travel & Accommodation module
// ATO 2026 travel allowance rates (Tax Determination TD 2026/1)
// ─────────────────────────────────────────────────────────────────

type ExpenseStatus   = 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'PAID'
type ExpenseCategory = 'MEALS' | 'ACCOMMODATION' | 'FUEL' | 'TOLLS' | 'PARKING' | 'FLIGHTS' | 'OTHER'

interface ExpenseClaim {
  id:            string
  claimNumber:   string
  employeeId:    string
  employeeName:  string
  jobId:         string
  jobNumber:     string
  expenseDate:   string
  category:      ExpenseCategory
  description:   string
  amountExGST:   number
  gst:           number
  totalIncGST:   number
  receiptRef:    string | null   // mock receipt reference
  status:        ExpenseStatus
  submittedAt:   string | null
  approvedById:  string | null
  approvedByName:string | null
  approvedAt:    string | null
  paidAt:        string | null
  notes:         string
}

const EXPENSE_CATEGORY_META: Record<ExpenseCategory, { label: string; icon: string; gstFree?: boolean }> = {
  MEALS:         { label: 'Meals allowance',   icon: '🍽️', gstFree: true },
  ACCOMMODATION: { label: 'Accommodation',     icon: '🛏️' },
  FUEL:          { label: 'Fuel',              icon: '⛽' },
  TOLLS:         { label: 'Tolls',             icon: '🛣️', gstFree: true },
  PARKING:       { label: 'Parking',           icon: '🅿️' },
  FLIGHTS:       { label: 'Flights',           icon: '✈️' },
  OTHER:         { label: 'Other',             icon: '📋' },
}

// ATO 2026 meal allowances (TD 2026/1)
const ATO_MEAL_RATES = { BREAKFAST: 29.60, LUNCH: 33.25, DINNER: 58.30, DAILY: 121.15 }

const SEED_EXPENSES: ExpenseClaim[] = [
  { id: 'exp-001', claimNumber: 'EXP-2026-001', employeeId: 'e3', employeeName: 'Sam Nguyen', jobId: 'j4', jobNumber: 'TP-2026-004', expenseDate: new Date(Date.now() - 3*86400000).toISOString().split('T')[0], category: 'MEALS', description: 'Meals allowance — 2 days away Annerley depot', amountExGST: 242.30, gst: 0, totalIncGST: 242.30, receiptRef: null, status: 'SUBMITTED', submittedAt: new Date(Date.now() - 2*86400000).toISOString(), approvedById: null, approvedByName: null, approvedAt: null, paidAt: null, notes: '' },
  { id: 'exp-002', claimNumber: 'EXP-2026-002', employeeId: 'e3', employeeName: 'Sam Nguyen', jobId: 'j4', jobNumber: 'TP-2026-004', expenseDate: new Date(Date.now() - 5*86400000).toISOString().split('T')[0], category: 'FUEL', description: 'Fuel — own vehicle to Annerley sub 3 trips', amountExGST: 68.20, gst: 6.82, totalIncGST: 75.02, receiptRef: 'RCPT-BP-20260804', status: 'APPROVED', submittedAt: new Date(Date.now() - 4*86400000).toISOString(), approvedById: 'u2', approvedByName: 'Jane Smith', approvedAt: new Date(Date.now() - 3*86400000).toISOString(), paidAt: null, notes: 'ATO rate 88c/km × 77.5km' },
  { id: 'exp-003', claimNumber: 'EXP-2026-003', employeeId: 'e1', employeeName: 'Tony Rivera', jobId: 'j1', jobNumber: 'TP-2026-001', expenseDate: new Date(Date.now() - 1*86400000).toISOString().split('T')[0], category: 'TOLLS', description: 'Legacy Way toll — 2 trips', amountExGST: 8.80, gst: 0, totalIncGST: 8.80, receiptRef: 'RCPT-LINKT-0041', status: 'DRAFT', submittedAt: null, approvedById: null, approvedByName: null, approvedAt: null, paidAt: null, notes: '' },
]

const EXPENSE_STATUS_META: Record<ExpenseStatus, { label: string; bg: string; text: string }> = {
  DRAFT:     { label: 'Draft',     bg: 'bg-gray-100',   text: 'text-gray-600'  },
  SUBMITTED: { label: 'Submitted', bg: 'bg-blue-100',   text: 'text-blue-700'  },
  APPROVED:  { label: 'Approved',  bg: 'bg-green-100',  text: 'text-green-700' },
  REJECTED:  { label: 'Rejected',  bg: 'bg-red-100',    text: 'text-red-700'   },
  PAID:      { label: 'Paid',      bg: 'bg-purple-100', text: 'text-purple-700'},
}

// ─────────────────────────────────────────────────────────────────
// EXPENSES TAB
// ─────────────────────────────────────────────────────────────────

function ExpensesTab({ canApprove, currentUserName, currentUserId }: {
  canApprove: boolean; currentUserName: string; currentUserId: string
}) {
  const [claims, setClaims]   = useState<ExpenseClaim[]>(SEED_EXPENSES)
  const [showNew, setShowNew] = useState(false)
  const [filterStatus, setFilter] = useState<ExpenseStatus | 'ALL'>('ALL')

  // New expense form state
  const [form, setForm] = useState({
    jobNumber: 'TP-2026-001', category: 'MEALS' as ExpenseCategory,
    date: new Date().toISOString().split('T')[0], description: '', amount: '', notes: '',
  })
  const setF = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))

  const filtered = claims.filter(c => filterStatus === 'ALL' || c.status === filterStatus)
  const pendingApproval = claims.filter(c => c.status === 'SUBMITTED').length

  const handleSubmitNew = (e: React.FormEvent) => {
    e.preventDefault()
    const cat = EXPENSE_CATEGORY_META[form.category]
    const amountExGST = parseFloat(form.amount) || 0
    const gstFree = cat.gstFree ?? false
    const gst = gstFree ? 0 : +(amountExGST / 11).toFixed(2)
    const seq = String(claims.length + 1).padStart(3, '0')
    const newClaim: ExpenseClaim = {
      id:            `exp-${Date.now()}`,
      claimNumber:   `EXP-2026-${seq}`,
      employeeId:    currentUserId,
      employeeName:  currentUserName,
      jobId:         '',
      jobNumber:     form.jobNumber,
      expenseDate:   form.date,
      category:      form.category,
      description:   form.description,
      amountExGST,
      gst,
      totalIncGST:   amountExGST + gst,
      receiptRef:    null,
      status:        'DRAFT',
      submittedAt:   null,
      approvedById:  null, approvedByName: null, approvedAt: null, paidAt: null,
      notes:         form.notes,
    }
    setClaims(prev => [newClaim, ...prev])
    setShowNew(false)
    setForm({ jobNumber: 'TP-2026-001', category: 'MEALS', date: new Date().toISOString().split('T')[0], description: '', amount: '', notes: '' })
  }

  const handleSubmit = (id: string) =>
    setClaims(prev => prev.map(c => c.id === id
      ? { ...c, status: 'SUBMITTED', submittedAt: new Date().toISOString() } : c))

  const handleApprove = (id: string) =>
    setClaims(prev => prev.map(c => c.id === id
      ? { ...c, status: 'APPROVED', approvedById: currentUserId, approvedByName: currentUserName, approvedAt: new Date().toISOString() } : c))

  const handleReject = (id: string) =>
    setClaims(prev => prev.map(c => c.id === id ? { ...c, status: 'REJECTED' } : c))

  const total = filtered.reduce((s, c) => s + c.totalIncGST, 0)

  return (
    <div className="space-y-4">
      {pendingApproval > 0 && canApprove && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-amber-800">
          <DollarSign className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <span><strong>{pendingApproval} expense claim{pendingApproval !== 1 ? 's' : ''} awaiting PM approval.</strong> Approve before payroll closes.</span>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1.5 flex-wrap">
          {(['ALL','DRAFT','SUBMITTED','APPROVED','PAID'] as const).map(s => {
            const count = s === 'ALL' ? claims.length : claims.filter(c => c.status === s).length
            if (count === 0 && s !== 'ALL') return null
            return (
              <button key={s} onClick={() => setFilter(s)}
                className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${
                  filterStatus === s ? 'bg-[#1F3864] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}>
                {s === 'ALL' ? 'All claims' : EXPENSE_STATUS_META[s].label} ({count})
              </button>
            )
          })}
        </div>
        <button onClick={() => setShowNew(v => !v)} className="btn-primary text-sm flex items-center gap-1.5">
          <Plus className="w-4 h-4" />New claim
        </button>
      </div>

      {/* New expense form */}
      {showNew && (
        <form onSubmit={handleSubmitNew} className="bg-blue-50 border border-blue-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-blue-900">New expense claim</h4>
            <button type="button" onClick={() => setShowNew(false)}><X className="w-4 h-4 text-gray-400" /></button>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div>
              <label className="label-sm">Job number</label>
              <select value={form.jobNumber} onChange={e => setF('jobNumber', e.target.value)} className="input text-sm w-full">
                {MOCK_JOBS.map(j => <option key={j.id} value={j.jobNumber}>{j.jobNumber}</option>)}
              </select>
            </div>
            <div>
              <label className="label-sm">Category</label>
              <select value={form.category} onChange={e => setF('category', e.target.value as ExpenseCategory)} className="input text-sm w-full">
                {Object.entries(EXPENSE_CATEGORY_META).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-sm">Date</label>
              <input type="date" value={form.date} onChange={e => setF('date', e.target.value)} className="input text-sm w-full" />
            </div>
            <div>
              <label className="label-sm">Amount (inc GST)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                <input type="number" step="0.01" value={form.amount} onChange={e => setF('amount', e.target.value)}
                  required placeholder="0.00" className="input pl-7 text-sm w-full" />
              </div>
            </div>
          </div>
          {form.category === 'MEALS' && (
            <div className="bg-white border border-blue-100 rounded-lg px-3 py-2 text-xs text-blue-700">
              ATO 2026 meal allowance rates: Breakfast ${ATO_MEAL_RATES.BREAKFAST} · Lunch ${ATO_MEAL_RATES.LUNCH} · Dinner ${ATO_MEAL_RATES.DINNER} · Full day ${ATO_MEAL_RATES.DAILY}. Meal allowances are GST-free.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 sm:col-span-1">
              <label className="label-sm">Description</label>
              <input value={form.description} onChange={e => setF('description', e.target.value)}
                required placeholder="Brief description of expense…" className="input text-sm w-full" />
            </div>
            <div>
              <label className="label-sm">Notes (optional)</label>
              <input value={form.notes} onChange={e => setF('notes', e.target.value)}
                placeholder="e.g. receipt reference, km calculation…" className="input text-sm w-full" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setShowNew(false)} className="btn-secondary text-sm">Cancel</button>
            <button type="submit" className="btn-primary text-sm flex items-center gap-2">
              <Receipt className="w-4 h-4" />Add expense
            </button>
          </div>
        </form>
      )}

      {/* Claims table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              {['Date', 'Employee', 'Job', 'Category', 'Description', 'Total inc GST', 'Status', 'Actions'].map(h => (
                <th key={h} className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map(c => {
              const sm = EXPENSE_STATUS_META[c.status]
              const cm = EXPENSE_CATEGORY_META[c.category]
              return (
                <tr key={c.id} className="hover:bg-gray-50/60">
                  <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{c.expenseDate}</td>
                  <td className="px-4 py-3 text-xs font-medium text-gray-900">{c.employeeName}</td>
                  <td className="px-4 py-3 text-xs font-mono text-gray-500">{c.jobNumber}</td>
                  <td className="px-4 py-3 text-xs">{cm.icon} {cm.label}</td>
                  <td className="px-4 py-3 text-xs text-gray-700 max-w-[180px] truncate">{c.description}</td>
                  <td className="px-4 py-3 text-xs font-semibold text-gray-900 font-mono whitespace-nowrap">${c.totalIncGST.toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sm.bg} ${sm.text}`}>{sm.label}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {c.status === 'DRAFT' && (
                        <button onClick={() => handleSubmit(c.id)}
                          className="text-xs text-blue-600 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded font-medium">Submit</button>
                      )}
                      {c.status === 'SUBMITTED' && canApprove && (
                        <>
                          <button onClick={() => handleApprove(c.id)}
                            className="text-xs text-green-700 bg-green-50 hover:bg-green-100 px-2 py-1 rounded font-medium">Approve</button>
                          <button onClick={() => handleReject(c.id)}
                            className="text-xs text-red-600 bg-red-50 hover:bg-red-100 px-2 py-1 rounded font-medium">Reject</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-gray-400 text-sm">No expense claims match the filter</td></tr>
            )}
          </tbody>
          {filtered.length > 0 && (
            <tfoot className="bg-gray-50 border-t">
              <tr>
                <td colSpan={5} className="px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wide">Total ({filtered.length} claim{filtered.length !== 1 ? 's' : ''})</td>
                <td className="px-4 py-2.5 text-sm font-bold text-gray-900 font-mono">${total.toFixed(2)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────

type FMFlagStatus = 'OPEN' | 'ACKNOWLEDGED' | 'RESOLVED' | 'PENDING_CONF'

interface FMFlag {
  id: string
  employeeId: string
  employeeName: string
  jobId: string
  jobNumber: string
  jobSite: string
  jobDescription: string
  homeSuburb: string
  assignedDepot: string
  distanceKm: number
  driveMins: number
  crossRegion: boolean
  consecutiveDays: number
  shiftType: string
  riskLevel: string
  raisedAt: string
  factors: string[]
  pmAcknowledgedAt: string | null
  pmAcknowledgedBy: string | null
  pmAction: string | null
  accommodationId: string | null
  employeeConfirmedAt: string | null
  status: FMFlagStatus
  notes: string
}
type Accommodation = typeof MOCK_ACCOMMODATION[0]
type PropertyType  = 'HOTEL' | 'MOTEL' | 'SERVICED_APARTMENT' | 'AIRBNB' | 'COMPANY_HOUSE' | 'OTHER'

const PROPERTY_TYPE_META: Record<PropertyType, { label: string; icon: string }> = {
  HOTEL:               { label: 'Hotel',                  icon: '🏨' },
  MOTEL:               { label: 'Motel',                  icon: '🏩' },
  SERVICED_APARTMENT:  { label: 'Serviced apartment',     icon: '🏢' },
  AIRBNB:              { label: 'Short-term rental / Airbnb', icon: '🏠' },
  COMPANY_HOUSE:       { label: 'MCB company house',      icon: '🏡' },
  OTHER:               { label: 'Other',                  icon: '📍' },
}

interface AssignedEmployee {
  employeeId:    string
  employeeName:  string
  assignedAt:    string
  assignedByName:string
  notifiedAt?:   string
  confirmedAt?:  string
  // Rollback audit trail
  rolledBackAt?:     string
  rolledBackByName?: string
  rollbackReason?:   string
  rollbackNotes?:    string
}

// Rollback reason options
const ROLLBACK_REASONS: { value: string; label: string }[] = [
  { value: 'ROSTER_CHANGE',          label: 'Roster change — employee reassigned to different site' },
  { value: 'JOB_CANCELLED',          label: 'Job cancelled or postponed' },
  { value: 'EMPLOYEE_UNAVAILABLE',   label: 'Employee unavailable (sick / personal)' },
  { value: 'ACCOMMODATION_CANCELLED',label: 'Accommodation booking cancelled' },
  { value: 'DATES_CHANGED',          label: 'Deployment dates changed' },
  { value: 'OTHER',                  label: 'Other (specify in notes)' },
]

interface EnhancedAccommodation {
  id:               string
  propertyName:     string
  propertyType:     PropertyType
  address:          string
  bookingReference?: string
  jobId?:           string
  jobNumber?:       string
  riskLevel:        string
  checkIn:          string
  checkOut:         string
  nightlyRateAud?:  number
  totalNights:      number
  totalBeds:        number                 // CRITICAL — hard capacity limit
  keyholderName:    string
  keyholderPhone:   string
  keyholderRole?:   string
  keyReturnProcedure?: string
  parkingAvailable: boolean
  kitchenAvailable: boolean
  wifiAvailable:    boolean
  laundryAvailable: boolean
  assignedEmployees: AssignedEmployee[]
  status:           'UPCOMING' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED'
  notes?:           string
  bookedByName:     string
  bookedAt:         string
}

// ─────────────────────────────────────────────────────────────────
// CONVERT EXISTING MOCK_ACCOMMODATION → ENHANCED FORMAT
// ─────────────────────────────────────────────────────────────────

const INITIAL_ENHANCED: EnhancedAccommodation[] = [
  {
    id: 'acc1', propertyName: 'The Woolstore Apartments', propertyType: 'SERVICED_APARTMENT',
    address: '88 Ipswich Rd, Woolloongabba QLD 4102',
    bookingReference: 'TWA-2026-0142',
    jobId: 'j4', jobNumber: 'TP-2026-004', riskLevel: 'RED',
    checkIn: '2026-06-20', checkOut: '2026-07-31', nightlyRateAud: 95, totalNights: 41,
    totalBeds: 2,
    keyholderName: 'Mia Torrence', keyholderPhone: '07 3000 1122', keyholderRole: 'Property manager',
    keyReturnProcedure: 'Return key to front desk reception before 10:00am on checkout day.',
    parkingAvailable: true, kitchenAvailable: true, wifiAvailable: true, laundryAvailable: false,
    assignedEmployees: [
      { employeeId: 'e3', employeeName: 'Sam Nguyen',   assignedAt: '2026-06-19T08:30:00Z', assignedByName: 'Jane Smith', notifiedAt: '2026-06-19T08:31:00Z', confirmedAt: '2026-06-19T09:00:00Z' },
    ],
    status: 'ACTIVE', bookedByName: 'Jane Smith', bookedAt: '2026-06-19T08:30:00Z',
    notes: 'Ground floor preferred. Secure parking on site. Includes kitchenette.',
  },
  {
    id: 'acc2', propertyName: 'Quest Broadbeach', propertyType: 'SERVICED_APARTMENT',
    address: '2685 Gold Coast Highway, Broadbeach QLD 4218',
    bookingReference: 'QB-2026-0058',
    jobId: 'j3', jobNumber: 'TP-2026-003', riskLevel: 'AMBER',
    checkIn: '2026-07-01', checkOut: '2026-07-03', nightlyRateAud: 145, totalNights: 2,
    totalBeds: 1,
    keyholderName: 'Quest Broadbeach Reception', keyholderPhone: '07 5539 9000', keyholderRole: 'Hotel reception',
    keyReturnProcedure: 'Return key card to reception on checkout.',
    parkingAvailable: true, kitchenAvailable: true, wifiAvailable: true, laundryAvailable: false,
    assignedEmployees: [
      { employeeId: 'e6', employeeName: 'Dave Fielding', assignedAt: '2026-06-25T10:30:00Z', assignedByName: 'Jane Smith', notifiedAt: '2026-06-25T10:31:00Z' },
    ],
    status: 'UPCOMING', bookedByName: 'Jane Smith', bookedAt: '2026-06-25T10:30:00Z',
    notes: "Dave's first away assignment. PM to call and confirm receipt.",
  },
]

// ─────────────────────────────────────────────────────────────────
// RISK BADGE (unchanged)
// ─────────────────────────────────────────────────────────────────

function RiskBadge({ level, size = 'sm' }: { level: string; size?: 'sm' | 'lg' }) {
  const rm = FM_RISK_META[level] ?? FM_RISK_META.GREEN
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium border ${rm.border} ${rm.bg} ${rm.text} ${size === 'lg' ? 'text-sm' : 'text-xs'}`}>
      <span>{rm.icon}</span>{rm.label}
    </span>
  )
}

// ─────────────────────────────────────────────────────────────────
// BOOK ACCOMMODATION MODAL — from FM flag (unchanged)
// ─────────────────────────────────────────────────────────────────

function BookAccommodationModal({
  flag, onClose, onBook,
}: { flag: FMFlag; onClose: () => void; onBook: (acc: any) => void }) {
  const [form, setForm] = useState({
    accommodationName: '', address: '', checkIn: '', checkOut: '',
    nightlyRateAud: '', totalNights: '', bookingReference: '', notes: '',
  })
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const nights = form.totalNights ? parseInt(form.totalNights) :
      form.checkIn && form.checkOut
        ? Math.ceil((new Date(form.checkOut).getTime() - new Date(form.checkIn).getTime()) / 86400000)
        : 0
    onBook({
      id: `acc_${Date.now()}`,
      fmFlagId: flag.id,
      employeeId: flag.employeeId, employeeName: flag.employeeName,
      jobId: flag.jobId, jobNumber: flag.jobNumber,
      accommodationName: form.accommodationName, address: form.address,
      bookingReference: form.bookingReference,
      checkIn: form.checkIn, checkOut: form.checkOut,
      nightlyRateAud: form.nightlyRateAud ? parseFloat(form.nightlyRateAud) : null,
      totalNights: nights,
      bookedByName: 'Jane PM', bookedAt: new Date().toISOString(),
      employeeConfirmed: false, employeeConfirmedAt: null,
      status: 'UPCOMING', notes: form.notes, riskLevel: flag.riskLevel,
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="font-semibold text-gray-900">Book accommodation</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {flag.employeeName} · {flag.jobNumber} · <RiskBadge level={flag.riskLevel} />
            </p>
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-500" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Accommodation name *</label>
            <input required className="input w-full text-sm" value={form.accommodationName}
              onChange={e => set('accommodationName', e.target.value)} placeholder="e.g. Quest Broadbeach" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Address *</label>
            <input required className="input w-full text-sm" value={form.address}
              onChange={e => set('address', e.target.value)} placeholder="Street address, Suburb QLD XXXX" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Check-in *</label>
              <input required type="date" className="input w-full text-sm" value={form.checkIn}
                onChange={e => set('checkIn', e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Check-out *</label>
              <input required type="date" className="input w-full text-sm" value={form.checkOut}
                onChange={e => set('checkOut', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Nightly rate (AUD)</label>
              <input type="number" className="input w-full text-sm" value={form.nightlyRateAud}
                onChange={e => set('nightlyRateAud', e.target.value)} placeholder="0.00" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Booking reference</label>
              <input className="input w-full text-sm" value={form.bookingReference}
                onChange={e => set('bookingReference', e.target.value)} placeholder="Confirmation #" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
            <textarea className="input w-full resize-none h-16 text-sm" value={form.notes}
              onChange={e => set('notes', e.target.value)} placeholder="Parking, access instructions…" />
          </div>
          <p className="text-xs text-gray-400">
            Once saved, {flag.employeeName} will be notified: name, address, check-in/out dates, and booking reference.
          </p>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button type="submit" className="btn-primary flex-1">Book &amp; notify employee</button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// NEW BOOKING MODAL — full field set (new feature)
// ─────────────────────────────────────────────────────────────────

function NewBookingModal({ onClose, onSave }: {
  onClose: () => void
  onSave:  (acc: EnhancedAccommodation) => void
}) {
  const { user } = useAuthStore()
  const [form, setForm] = useState({
    propertyName: '', propertyType: 'HOTEL' as PropertyType,
    address: '', bookingReference: '',
    jobId: '', riskLevel: 'GREEN',
    checkIn: '', checkOut: '',
    nightlyRateAud: '',
    totalBeds: '1',
    keyholderName: '', keyholderPhone: '', keyholderRole: '', keyReturnProcedure: '',
    parkingAvailable: false, kitchenAvailable: false, wifiAvailable: false, laundryAvailable: false,
    notes: '',
    assignees: [] as string[],
  })
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }))

  const totalNights = useMemo(() => {
    if (!form.checkIn || !form.checkOut) return 0
    return Math.max(0, Math.ceil((new Date(form.checkOut).getTime() - new Date(form.checkIn).getTime()) / 86400000))
  }, [form.checkIn, form.checkOut])

  const bedsLeft = parseInt(form.totalBeds || '0') - form.assignees.length
  const job = MOCK_JOBS.find(j => j.id === form.jobId)

  const toggleAssignee = (id: string) => {
    if (form.assignees.includes(id)) {
      set('assignees', form.assignees.filter(a => a !== id))
    } else if (form.assignees.length < parseInt(form.totalBeds || '0')) {
      set('assignees', [...form.assignees, id])
    }
  }

  const canSave = form.propertyName.trim() && form.address.trim() && form.checkIn && form.checkOut && form.keyholderName.trim() && form.keyholderPhone.trim()

  const handleSave = () => {
    if (!canSave) return
    const now = new Date().toISOString()
    onSave({
      id:               `acc_${Date.now()}`,
      propertyName:     form.propertyName.trim(),
      propertyType:     form.propertyType,
      address:          form.address.trim(),
      bookingReference: form.bookingReference.trim() || undefined,
      jobId:            form.jobId || undefined,
      jobNumber:        job?.jobNumber,
      riskLevel:        form.riskLevel,
      checkIn:          form.checkIn,
      checkOut:         form.checkOut,
      nightlyRateAud:   form.nightlyRateAud ? parseFloat(form.nightlyRateAud) : undefined,
      totalNights,
      totalBeds:        parseInt(form.totalBeds) || 1,
      keyholderName:    form.keyholderName.trim(),
      keyholderPhone:   form.keyholderPhone.trim(),
      keyholderRole:    form.keyholderRole.trim() || undefined,
      keyReturnProcedure: form.keyReturnProcedure.trim() || undefined,
      parkingAvailable: form.parkingAvailable,
      kitchenAvailable: form.kitchenAvailable,
      wifiAvailable:    form.wifiAvailable,
      laundryAvailable: form.laundryAvailable,
      assignedEmployees: form.assignees.map(id => {
        const emp = MOCK_EMPLOYEES.find(e => e.id === id)!
        return { employeeId: id, employeeName: emp.fullName, assignedAt: now, assignedByName: user?.fullName ?? 'PM', notifiedAt: now }
      }),
      status:      form.checkIn <= new Date().toISOString().slice(0, 10) ? 'ACTIVE' : 'UPCOMING',
      notes:       form.notes.trim() || undefined,
      bookedByName: user?.fullName ?? 'PM',
      bookedAt:    now,
    })
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-4">

        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-semibold text-gray-900">New accommodation booking</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="p-6 space-y-5 max-h-[78vh] overflow-y-auto">

          {/* ── Property details ── */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Property details</p>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Property name *</label>
                  <input value={form.propertyName} onChange={e => set('propertyName', e.target.value)}
                    className="input w-full text-sm" placeholder="e.g. Quest Broadbeach" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Property type *</label>
                  <select value={form.propertyType} onChange={e => set('propertyType', e.target.value)} className="input w-full text-sm">
                    {(Object.keys(PROPERTY_TYPE_META) as PropertyType[]).map(t => (
                      <option key={t} value={t}>{PROPERTY_TYPE_META[t].icon} {PROPERTY_TYPE_META[t].label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Street address *</label>
                <input value={form.address} onChange={e => set('address', e.target.value)}
                  className="input w-full text-sm" placeholder="Full street address, suburb, state, postcode" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Booking reference</label>
                  <input value={form.bookingReference} onChange={e => set('bookingReference', e.target.value)}
                    className="input w-full text-sm" placeholder="Confirmation #" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Project / job *</label>
                  <select value={form.jobId} onChange={e => set('jobId', e.target.value)} className="input w-full text-sm">
                    <option value="">— Select job —</option>
                    {MOCK_JOBS.map(j => <option key={j.id} value={j.id}>{j.jobNumber}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* ── Dates & capacity ── */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Dates &amp; capacity</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Check-in *</label>
                <input type="date" value={form.checkIn} onChange={e => set('checkIn', e.target.value)} className="input w-full text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Check-out *</label>
                <input type="date" value={form.checkOut} onChange={e => set('checkOut', e.target.value)} className="input w-full text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nightly rate ($)</label>
                <input type="number" value={form.nightlyRateAud} onChange={e => set('nightlyRateAud', e.target.value)}
                  className="input w-full text-sm" placeholder="0.00" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                  <Bed className="w-3.5 h-3.5" /> Beds available *
                </label>
                <select value={form.totalBeds} onChange={e => set('totalBeds', e.target.value)} className="input w-full text-sm">
                  {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n} bed{n!==1?'s':''}</option>)}
                </select>
              </div>
            </div>
            {totalNights > 0 && (
              <div className="mt-2 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                {totalNights} night{totalNights!==1?'s':''} total
                {form.nightlyRateAud ? ` · Est. total: ${formatCurrency(parseFloat(form.nightlyRateAud) * totalNights)}` : ''}
              </div>
            )}
          </div>

          {/* ── Keyholder ── */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Key className="w-3.5 h-3.5" /> Keyholder details
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Keyholder name *</label>
                  <input value={form.keyholderName} onChange={e => set('keyholderName', e.target.value)}
                    className="input w-full text-sm" placeholder="Full name" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Keyholder phone *</label>
                  <input value={form.keyholderPhone} onChange={e => set('keyholderPhone', e.target.value)}
                    className="input w-full text-sm" placeholder="07 XXXX XXXX" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
                <input value={form.keyholderRole} onChange={e => set('keyholderRole', e.target.value)}
                  className="input w-full text-sm" placeholder="e.g. Property manager, Site admin, PM — Jane Smith" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Key return procedure on checkout</label>
                <input value={form.keyReturnProcedure} onChange={e => set('keyReturnProcedure', e.target.value)}
                  className="input w-full text-sm" placeholder="e.g. Return to reception, drop in letterbox, return to depot" />
              </div>
            </div>
          </div>

          {/* ── Amenities ── */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Amenities</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { key: 'parkingAvailable', label: 'Parking', icon: '🚗' },
                { key: 'kitchenAvailable', label: 'Kitchen', icon: '🍳' },
                { key: 'wifiAvailable',    label: 'WiFi',    icon: '📶' },
                { key: 'laundryAvailable', label: 'Laundry', icon: '🫧' },
              ].map(a => (
                <label key={a.key} onClick={() => set(a.key, !(form as any)[a.key])}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 cursor-pointer transition-colors ${(form as any)[a.key] ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <span className="text-base">{a.icon}</span>
                  <span className="text-sm font-medium">{a.label}</span>
                  {(form as any)[a.key] && <CheckCircle className="w-4 h-4 text-green-500 ml-auto" />}
                </label>
              ))}
            </div>
          </div>

          {/* ── Assign employees ── */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> Assign employees
              <span className="ml-auto font-normal text-gray-500 normal-case tracking-normal">
                {form.assignees.length}/{form.totalBeds} beds — {bedsLeft} remaining
              </span>
            </p>
            <div className="border border-gray-200 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
              {MOCK_EMPLOYEES.map(emp => {
                const selected = form.assignees.includes(emp.id)
                const atCapacity = !selected && form.assignees.length >= parseInt(form.totalBeds || '0')
                return (
                  <label key={emp.id}
                    className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer border-b border-gray-100 last:border-0 transition-colors ${selected ? 'bg-indigo-50' : atCapacity ? 'bg-gray-50 opacity-50' : 'hover:bg-gray-50'}`}>
                    <input type="checkbox" checked={selected} disabled={atCapacity && !selected}
                      onChange={() => toggleAssignee(emp.id)}
                      className="w-4 h-4 accent-indigo-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{emp.fullName}</p>
                      <p className="text-xs text-gray-400">{emp.payClassification?.replace(/_/g,' ')}</p>
                    </div>
                    {selected && <CheckCircle className="w-4 h-4 text-indigo-500 flex-shrink-0" />}
                  </label>
                )
              })}
            </div>
            {form.assignees.length > 0 && (
              <div className="mt-2 text-xs text-indigo-700 bg-indigo-50 rounded-lg px-3 py-2 flex items-center gap-1.5">
                <Smartphone className="w-3.5 h-3.5" />
                {form.assignees.length} employee{form.assignees.length!==1?'s':''} will receive a mobile push notification with all accommodation details on save.
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
              className="input w-full resize-none h-16 text-sm"
              placeholder="Parking arrangements, access codes, quiet hours, pet policy…" />
          </div>
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-white">Cancel</button>
          <button onClick={handleSave} disabled={!canSave}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg disabled:opacity-40 transition-colors">
            <Bed className="w-4 h-4" /> Create booking &amp; notify
          </button>
        </div>
      </div>
    </div>
  )
}


// ─────────────────────────────────────────────────────────────────
// ROSTER STATUS HELPER
// Checks whether an employee's current MOCK_ALLOCATIONS still
// match the job this accommodation was booked for.
// ─────────────────────────────────────────────────────────────────
function getRosterMatchStatus(employeeId: string, jobId?: string): {
  matched: boolean; currentJobId?: string; currentJobNumber?: string; dayCount: number
} {
  const allocs = MOCK_ALLOCATIONS.filter(a => a.employeeId === employeeId && WEEK_DAYS.includes(a.date))
  if (allocs.length === 0) return { matched: false, dayCount: 0 }
  const onThisJob = allocs.filter(a => a.jobId === jobId)
  const onOtherJob = allocs.find(a => a.jobId !== jobId)
  if (onThisJob.length > 0) return { matched: true, dayCount: onThisJob.length }
  if (onOtherJob) {
    const job = MOCK_JOBS.find(j => j.id === onOtherJob.jobId)
    return { matched: false, currentJobId: onOtherJob.jobId, currentJobNumber: job?.jobNumber, dayCount: allocs.length }
  }
  return { matched: false, dayCount: 0 }
}

// ─────────────────────────────────────────────────────────────────
// ROLLBACK CONFIRM MODAL
// PM confirms the rollback with a reason before unassigning.
// Shows current roster status for context.
// ─────────────────────────────────────────────────────────────────
function RollbackConfirmModal({ acc, ae, onConfirm, onClose }: {
  acc:       EnhancedAccommodation
  ae:        AssignedEmployee
  onConfirm: (reason: string, reasonLabel: string, notes: string | null) => void
  onClose:   () => void
}) {
  const [reason, setReason] = useState('ROSTER_CHANGE')
  const [notes, setNotes]   = useState('')
  const rosterStatus = getRosterMatchStatus(ae.employeeId, acc.jobId)
  const selectedReason = ROLLBACK_REASONS.find(r => r.value === reason)!

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-amber-50">
          <div>
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <RotateCcw className="w-4 h-4 text-amber-600" /> Rollback accommodation assignment
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">{ae.employeeName} · {acc.propertyName}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="p-6 space-y-4">

          {/* Roster status context */}
          <div className={`rounded-xl border px-4 py-3 ${rosterStatus.matched ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
            <p className="text-xs font-semibold mb-1 flex items-center gap-1.5">
              {rosterStatus.matched
                ? <><CheckCircle className="w-3.5 h-3.5 text-green-600" /><span className="text-green-800">Roster currently matches this booking</span></>
                : <><AlertCircle className="w-3.5 h-3.5 text-amber-600" /><span className="text-amber-800">Roster has changed — accommodation may no longer be needed</span></>
              }
            </p>
            <p className="text-xs text-gray-600">
              {rosterStatus.matched
                ? `${ae.employeeName} is allocated to ${acc.jobNumber} for ${rosterStatus.dayCount} day${rosterStatus.dayCount !== 1 ? 's' : ''} this week.`
                : rosterStatus.currentJobNumber
                  ? `${ae.employeeName} is now allocated to ${rosterStatus.currentJobNumber}, not to ${acc.jobNumber ?? 'this job'}.`
                  : `${ae.employeeName} has no active roster allocation this week.`
              }
            </p>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Reason for rollback *</label>
            <select className="input w-full text-sm" value={reason} onChange={e => setReason(e.target.value)}>
              {ROLLBACK_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
            <textarea className="input w-full resize-none h-16 text-sm" value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Add context — roster change dates, alternative arrangement, cancellation reference…" />
          </div>

          {/* Accommodation dates reminder */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-xs text-gray-600">
            <div className="font-semibold mb-1 flex items-center gap-1.5">
              <Info className="w-3 h-3" /> Booking will remain active
            </div>
            {acc.propertyName} · Check-in: {formatDate(acc.checkIn)} → Check-out: {formatDate(acc.checkOut)} ({acc.totalNights} nights)
            <br />
            Unassigning {ae.employeeName} will free up 1 bed ({acc.totalBeds - acc.assignedEmployees.length + 1} available after rollback).
            The booking itself is NOT cancelled — update or cancel separately if required.
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button
            onClick={() => onConfirm(reason, selectedReason.label, notes.trim() || null)}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition-colors">
            <RotateCcw className="w-4 h-4" /> Confirm rollback
          </button>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// ASSIGN EMPLOYEES PANEL (inline within card)
// ─────────────────────────────────────────────────────────────────

function AssignEmployeesPanel({ acc, allAcc, onAssign, onUnassign }: {
  acc:       EnhancedAccommodation
  allAcc:    EnhancedAccommodation[]
  onAssign:  (acc: EnhancedAccommodation, empId: string) => void
  onUnassign:(acc: EnhancedAccommodation, empId: string, reason: string, reasonLabel: string, notes: string | null) => void
}) {
  const { user } = useAuthStore()
  const [selId, setSelId]   = useState('')
  const [rollbackTarget, setRollbackTarget] = useState<AssignedEmployee | null>(null)

  const assignedIds = acc.assignedEmployees.map(e => e.employeeId)
  const bedsLeft    = acc.totalBeds - assignedIds.length
  // employees not already in any active booking
  const activeAssignedIds = new Set(allAcc.filter(a => a.status !== 'COMPLETED' && a.id !== acc.id).flatMap(a => a.assignedEmployees.map(e => e.employeeId)))
  const available   = MOCK_EMPLOYEES.filter(e => !assignedIds.includes(e.id) && !activeAssignedIds.has(e.id))

  const handleAdd = () => {
    if (!selId || bedsLeft <= 0) return
    onAssign(acc, selId)
    setSelId('')
  }

  return (
    <div className="mt-3 border-t pt-3 space-y-3">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
        Assign employees — {assignedIds.length}/{acc.totalBeds} beds occupied
      </p>

      {/* Progress bar */}
      <div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${assignedIds.length >= acc.totalBeds ? 'bg-red-500' : assignedIds.length >= acc.totalBeds * 0.8 ? 'bg-amber-400' : 'bg-green-500'}`}
            style={{ width: `${acc.totalBeds > 0 ? (assignedIds.length / acc.totalBeds) * 100 : 0}%` }}
          />
        </div>
        <p className="text-xs text-gray-400 mt-1">{bedsLeft} bed{bedsLeft !== 1 ? 's' : ''} remaining</p>
      </div>

      {/* Current occupants */}
      {acc.assignedEmployees.map(ae => {
        const rosterStatus = getRosterMatchStatus(ae.employeeId, acc.jobId)
        return (
          <div key={ae.employeeId} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${!rosterStatus.matched ? 'bg-amber-50 border border-amber-200' : ''}`}>
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${ae.confirmedAt ? 'bg-green-500' : ae.notifiedAt ? 'bg-amber-400' : 'bg-gray-300'}`} />
            <div className="flex-1 min-w-0">
              <span className="text-sm text-gray-700">{ae.employeeName}</span>
              {!rosterStatus.matched && (
                <div className="text-xs text-amber-700 flex items-center gap-1 mt-0.5">
                  <AlertCircle className="w-3 h-3" />
                  {rosterStatus.currentJobNumber
                    ? `Now rostered to ${rosterStatus.currentJobNumber}`
                    : 'No active roster this week'}
                </div>
              )}
            </div>
            <span className="text-xs text-gray-400 flex-shrink-0">
              {ae.confirmedAt ? '✓ Confirmed' : ae.notifiedAt ? '📱 Notified' : 'Pending'}
            </span>
            <button onClick={() => setRollbackTarget(ae)}
              className={`p-1.5 transition-colors rounded-lg flex-shrink-0 ${!rosterStatus.matched ? 'text-amber-500 bg-amber-100 hover:bg-amber-200' : 'text-gray-300 hover:text-amber-500 hover:bg-amber-50'}`}
              title="Rollback assignment">
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          </div>
        )
      })}

      {/* Add employee */}
      {bedsLeft > 0 && available.length > 0 && (
        <div className="flex gap-2">
          <select value={selId} onChange={e => setSelId(e.target.value)} className="input flex-1 text-sm">
            <option value="">— Add employee —</option>
            {available.map(e => <option key={e.id} value={e.id}>{e.fullName}</option>)}
          </select>
          <button onClick={handleAdd} disabled={!selId}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded-lg disabled:opacity-40 transition-colors">
            <UserPlus className="w-3.5 h-3.5" /> Assign &amp; notify
          </button>
        </div>
      )}
      {bedsLeft <= 0 && <p className="text-xs text-red-600 font-medium">At capacity — all {acc.totalBeds} beds occupied</p>}

      {/* Rollback confirm modal */}
      {rollbackTarget && (
        <RollbackConfirmModal
          acc={acc}
          ae={rollbackTarget}
          onConfirm={(reason, reasonLabel, notes) => {
            onUnassign(acc, rollbackTarget.employeeId, reason, reasonLabel, notes)
            setRollbackTarget(null)
          }}
          onClose={() => setRollbackTarget(null)}
        />
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// MOBILE PUSH PREVIEW
// ─────────────────────────────────────────────────────────────────

function MobilePushPreview({ acc, employeeName, onClose }: {
  acc: EnhancedAccommodation; employeeName: string; onClose: () => void
}) {
  const coOccupants = acc.assignedEmployees.filter(e => e.employeeName !== employeeName)
  const pt = PROPERTY_TYPE_META[acc.propertyType]
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="bg-gray-900 px-5 py-3 flex items-center justify-between">
          <p className="text-white text-xs font-medium">TrafficProcure · Accommodation details</p>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-4 max-h-[80vh] overflow-y-auto">
          <div className="flex items-start gap-3">
            <span className="text-3xl">{pt.icon}</span>
            <div>
              <p className="font-bold text-gray-900">{acc.propertyName}</p>
              <p className="text-xs text-gray-500">{pt.label}</p>
            </div>
          </div>
          <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
            <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" /><span className="text-gray-700">{acc.address}</span></div>
            <div className="flex items-center gap-2"><Bed className="w-4 h-4 text-gray-400" /><span>Check in: <strong>{formatDate(acc.checkIn)}</strong> &rarr; Check out: <strong>{formatDate(acc.checkOut)}</strong> ({acc.totalNights} nights)</span></div>
            {acc.bookingReference && <div className="text-xs text-gray-400">Ref: {acc.bookingReference}</div>}
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-1">
            <p className="text-xs font-semibold text-amber-800 flex items-center gap-1"><Key className="w-3.5 h-3.5" /> KEY ACCESS</p>
            <p className="text-sm font-bold text-gray-900">{acc.keyholderName}</p>
            {acc.keyholderRole && <p className="text-xs text-gray-600">{acc.keyholderRole}</p>}
            <a href={`tel:${acc.keyholderPhone}`} className="flex items-center gap-1.5 text-indigo-600 text-sm font-medium">
              <Phone className="w-3.5 h-3.5" />{acc.keyholderPhone}
            </a>
            {acc.keyReturnProcedure && (
              <p className="text-xs text-gray-500 mt-1"><strong>Return keys:</strong> {acc.keyReturnProcedure}</p>
            )}
          </div>

          {coOccupants.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-blue-700 mb-2 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> CO-OCCUPANTS</p>
              {coOccupants.map(e => <p key={e.employeeId} className="text-sm text-gray-700">{e.employeeName}</p>)}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {acc.parkingAvailable && <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-lg">🚗 Parking</span>}
            {acc.kitchenAvailable && <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-lg">🍳 Kitchen</span>}
            {acc.wifiAvailable    && <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-lg">📶 WiFi</span>}
            {acc.laundryAvailable && <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-lg">🫧 Laundry</span>}
          </div>

          <div className="pt-2 border-t">
            <button className="w-full py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium">
              ✓ Confirm receipt
            </button>
            <p className="text-xs text-gray-400 text-center mt-2">Tap to confirm you have received these details</p>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// ENHANCED ACCOMMODATION CARD
// ─────────────────────────────────────────────────────────────────

function EnhancedAccommodationCard({ acc, allAcc, onAssign, onUnassign, onResend, canAct }: {
  acc:         EnhancedAccommodation
  allAcc:      EnhancedAccommodation[]
  onAssign:    (acc: EnhancedAccommodation, empId: string) => void
  onUnassign:  (acc: EnhancedAccommodation, empId: string, reason: string, reasonLabel: string, notes: string | null) => void
  onResend:    (acc: EnhancedAccommodation, empId: string) => void
  canAct:      boolean
}) {
  const [showAssign,  setShowAssign]  = useState(false)
  const [pushPreview, setPushPreview] = useState<string | null>(null) // employeeName

  const sm = ACCOMMODATION_STATUS_META[acc.status] ?? ACCOMMODATION_STATUS_META.UPCOMING
  const rm = FM_RISK_META[acc.riskLevel] ?? FM_RISK_META.GREEN
  const pt = PROPERTY_TYPE_META[acc.propertyType]
  const today = new Date('2026-06-27')
  const checkOut = new Date(acc.checkOut)
  const daysLeft = Math.ceil((checkOut.getTime() - today.getTime()) / 86400000)
  const isExpiring = daysLeft >= 0 && daysLeft <= 2 && acc.status === 'ACTIVE'
  const totalCost = acc.nightlyRateAud ? acc.nightlyRateAud * acc.totalNights : null
  const bedsOccupied = acc.assignedEmployees.length
  const bedsPct = acc.totalBeds > 0 ? (bedsOccupied / acc.totalBeds) * 100 : 0
  const unconfirmed = acc.assignedEmployees.filter(e => !e.confirmedAt)

  return (
    <div className={`bg-white border-2 ${isExpiring ? 'border-red-400' : 'border-gray-200'} rounded-xl overflow-hidden`}>
      <div className="px-5 py-4">

        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-base">{pt.icon}</span>
              <span className="font-bold text-gray-900">{acc.propertyName}</span>
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${sm.bg} ${sm.text}`}>{sm.label}</span>
              <RiskBadge level={acc.riskLevel} />
              {isExpiring && (
                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold animate-pulse">
                  Check-out in {daysLeft}d
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 flex items-center gap-1"><MapPin className="w-3 h-3" />{acc.address}</p>
            {acc.jobNumber && <p className="text-xs text-indigo-600 mt-0.5">{acc.jobNumber}</p>}
          </div>
          <div className="text-right flex-shrink-0">
            {acc.bookingReference && <div className="text-xs font-bold text-gray-700">Ref: {acc.bookingReference}</div>}
            {totalCost && <div className="text-xs text-gray-400">{formatCurrency(totalCost)} est.</div>}
          </div>
        </div>

        {/* Dates strip */}
        <div className="mt-3 grid grid-cols-4 gap-2 text-sm border-t pt-3">
          <div><div className="text-xs text-gray-400">Check-in</div><div className="font-medium text-xs">{formatDate(acc.checkIn)}</div></div>
          <div><div className="text-xs text-gray-400">Check-out</div><div className="font-medium text-xs">{formatDate(acc.checkOut)}</div></div>
          <div><div className="text-xs text-gray-400">Nights</div><div className="font-medium text-xs">{acc.totalNights}{acc.nightlyRateAud ? ` · $${acc.nightlyRateAud}/n` : ''}</div></div>
          <div><div className="text-xs text-gray-400">Type</div><div className="font-medium text-xs">{pt.label}</div></div>
        </div>

        {/* Bed occupancy */}
        <div className="mt-3 border-t pt-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
              <Bed className="w-3.5 h-3.5" /> {bedsOccupied} of {acc.totalBeds} bed{acc.totalBeds!==1?'s':''} occupied
            </span>
            <span className={`text-xs font-bold ${bedsPct >= 100 ? 'text-red-600' : bedsPct >= 80 ? 'text-amber-600' : 'text-green-600'}`}>
              {acc.totalBeds - bedsOccupied} remaining
            </span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${bedsPct >= 100 ? 'bg-red-500' : bedsPct >= 80 ? 'bg-amber-400' : 'bg-green-500'}`}
              style={{ width: `${Math.min(bedsPct, 100)}%` }} />
          </div>

          {/* Assigned employees */}
          {acc.assignedEmployees.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {acc.assignedEmployees.map(ae => (
                <div key={ae.employeeId}
                  className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border cursor-pointer
                    ${ae.confirmedAt ? 'bg-green-50 border-green-200 text-green-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}
                  onClick={() => setPushPreview(ae.employeeName)}
                  title="Preview mobile notification">
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: ae.confirmedAt ? '#22c55e' : '#f59e0b' }} />
                  {ae.employeeName}
                  {ae.confirmedAt ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Keyholder */}
        <div className="mt-3 border-t pt-3 flex items-center gap-3 bg-amber-50 rounded-lg px-3 py-2.5">
          <Key className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-amber-900">{acc.keyholderName}</p>
            {acc.keyholderRole && <p className="text-xs text-amber-700">{acc.keyholderRole}</p>}
          </div>
          <a href={`tel:${acc.keyholderPhone}`} className="flex items-center gap-1 text-xs text-indigo-600 font-medium whitespace-nowrap">
            <Phone className="w-3.5 h-3.5" />{acc.keyholderPhone}
          </a>
        </div>

        {/* Amenities */}
        <div className="mt-2 flex gap-2 flex-wrap">
          {acc.parkingAvailable && <span className="text-xs text-gray-500">🚗 Parking</span>}
          {acc.kitchenAvailable && <span className="text-xs text-gray-500">🍳 Kitchen</span>}
          {acc.wifiAvailable    && <span className="text-xs text-gray-500">📶 WiFi</span>}
          {acc.laundryAvailable && <span className="text-xs text-gray-500">🫧 Laundry</span>}
        </div>

        {/* Unconfirmed alert */}
        {unconfirmed.length > 0 && (
          <div className="mt-2 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 border border-amber-200">
            <Clock className="w-3.5 h-3.5 flex-shrink-0" />
            {unconfirmed.length} employee{unconfirmed.length!==1?'s':''} haven't confirmed receipt
            {canAct && (
              <button onClick={() => unconfirmed.forEach(e => onResend(acc, e.employeeId))}
                className="ml-auto flex items-center gap-1 underline underline-offset-2 hover:text-amber-900">
                <RefreshCw className="w-3 h-3" /> Re-send
              </button>
            )}
          </div>
        )}

        {/* Roster change warning */}
        {(() => {
          const rosterMismatches = acc.assignedEmployees.filter(ae => {
            const s = getRosterMatchStatus(ae.employeeId, acc.jobId)
            return !s.matched
          })
          if (rosterMismatches.length === 0) return null
          return (
            <div className="mt-2 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 rounded-lg px-3 py-2 border border-amber-200">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold">Roster change detected — </span>
                {rosterMismatches.map(e => e.employeeName).join(', ')} {rosterMismatches.length === 1 ? 'is' : 'are'} no longer rostered to {acc.jobNumber ?? 'this job'}.
                Review and rollback if accommodation is no longer required.
              </div>
            </div>
          )
        })()}

        {/* Actions */}
        {canAct && (
          <div className="mt-3 border-t pt-3 flex gap-2">
            <button onClick={() => setShowAssign(!showAssign)}
              className="flex items-center gap-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg border border-indigo-200 transition-colors">
              <UserPlus className="w-3.5 h-3.5" /> Assign employees
            </button>
            {acc.assignedEmployees.length > 0 && (
              <div className="relative group">
                <button className="flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded-lg border border-emerald-200 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                  Print confirmations
                </button>
                <div className="absolute left-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-10 min-w-48 hidden group-hover:block">
                  {acc.assignedEmployees.map(ae => (
                    <button key={ae.employeeId} onClick={() => generateAccommodationConfirmation(acc, ae.employeeName)}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 first:rounded-t-lg last:rounded-b-lg border-b border-slate-100 last:border-0">
                      {ae.employeeName}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Assign panel */}
        {showAssign && canAct && (
          <AssignEmployeesPanel acc={acc} allAcc={allAcc} onAssign={onAssign} onUnassign={onUnassign} />
        )}

        {acc.notes && <p className="mt-2 text-xs text-gray-400 italic">{acc.notes}</p>}
        <div className="mt-1 text-xs text-gray-400">Booked by {acc.bookedByName} · {formatDate(acc.bookedAt)}</div>
      </div>

      {/* Mobile push preview */}
      {pushPreview && (
        <MobilePushPreview acc={acc} employeeName={pushPreview} onClose={() => setPushPreview(null)} />
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// FM FLAG CARD (unchanged)
// ─────────────────────────────────────────────────────────────────

function FMFlagCard({
  flag, onAcknowledge, onBook, onMarkEmployeeOwn, canAct,
}: { flag: FMFlag; onAcknowledge: (id: string) => void; onBook: (flag: FMFlag) => void; onMarkEmployeeOwn: (id: string) => void; canAct: boolean }) {
  const [expanded, setExpanded] = useState(flag.riskLevel === 'RED')
  const fmp = EMPLOYEE_FM_PROFILES[flag.employeeId]
  const rm  = FM_RISK_META[flag.riskLevel] ?? FM_RISK_META.GREEN
  const sm  = FM_STATUS_META[flag.status]  ?? FM_STATUS_META.OPEN

  return (
    <div className={`bg-white border-2 ${rm.border} rounded-xl overflow-hidden`}>
      <button onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-gray-50 transition-colors">
        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${rm.dot}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className="font-bold text-gray-900">{flag.employeeName}</span>
            <RiskBadge level={flag.riskLevel} />
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${sm.bg} ${sm.text}`}>{sm.label}</span>
          </div>
          <p className="text-xs text-gray-500">
            {flag.jobNumber} · {flag.consecutiveDays} consecutive days · {flag.distanceKm}km from home · {flag.jobSite}
          </p>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
      </button>

      {expanded && (
        <div className="border-t px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            {[
              { label: 'Distance from home', value: `${flag.distanceKm}km` },
              { label: 'Consecutive days',   value: `${flag.consecutiveDays} of max ${FM_THRESHOLDS.CONSECUTIVE_MAX}` },
              { label: 'Deployment site',    value: flag.jobSite },
              { label: 'Home suburb',        value: flag.homeSuburb },
              { label: 'Assigned depot',     value: fmp ? SITES[fmp.assignedDepot]?.label : '—' },
              { label: 'Accommodation pref', value: fmp?.accommodationPreference ?? '—' },
            ].map(f => (
              <div key={f.label}>
                <div className="text-xs text-gray-400">{f.label}</div>
                <div className="font-medium">{f.value}</div>
              </div>
            ))}
          </div>

          {flag.notes && (
            <div className="text-xs text-gray-600 bg-blue-50 rounded-lg px-3 py-2">
              <strong>Notes:</strong> {flag.notes}
            </div>
          )}

          {flag.employeeConfirmedAt && (
            <div className="text-xs text-green-700 bg-green-50 rounded-lg px-3 py-2 flex items-center gap-1.5">
              <CheckCircle className="w-3.5 h-3.5" />
              Employee confirmed: {new Date(flag.employeeConfirmedAt).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </div>
          )}

          {canAct && ['OPEN','PENDING_CONF'].includes(flag.status) && (
            <div className="flex flex-wrap gap-2 pt-1">
              {flag.status === 'OPEN' && (
                <button onClick={() => onAcknowledge(flag.id)} className="btn-secondary text-sm">
                  Acknowledge flag
                </button>
              )}
              {['OPEN','ACKNOWLEDGED'].includes(flag.status) && (
                <button onClick={() => onBook(flag as any)}
                  className="btn-primary text-sm flex items-center gap-1.5">
                  <Bed className="w-4 h-4" /> Book accommodation
                </button>
              )}
              {flag.status === 'OPEN' && (
                <button onClick={() => onMarkEmployeeOwn(flag.id)} className="btn-secondary text-sm">
                  Employee arranging own
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// AWAY REGISTER TAB (unchanged)
// ─────────────────────────────────────────────────────────────────

function AwayRegisterTab() {
  if (MOCK_AWAY_REGISTER.length === 0) {
    return <p className="text-sm">No employees currently away from home depot</p>
  }
  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-500 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
        <strong>Away Register</strong> — employees currently deployed away from their home depot.
        Fatigue Management thresholds: Yellow at {FM_THRESHOLDS.DISTANCE_YELLOW_KM}km · Amber at {FM_THRESHOLDS.DISTANCE_AMBER_KM}km · mandatory rest after {FM_THRESHOLDS.CONSECUTIVE_MAX} consecutive days.
      </div>
      {MOCK_AWAY_REGISTER.map(entry => {
        const rm  = FM_RISK_META[entry.riskLevel] ?? FM_RISK_META.GREEN
        const acc = INITIAL_ENHANCED.find(a => a.id === entry.accommodationId)
        return (
          <div key={entry.employeeId} className={`bg-white border-2 ${rm.border} rounded-xl overflow-hidden`}>
            <div className="px-5 py-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-bold text-gray-900 text-base">{entry.employeeName}</span>
                    <RiskBadge level={entry.riskLevel} />
                  </div>
                  <p className="text-xs text-gray-500">{entry.currentJobNumber} · {entry.currentSite}</p>
                </div>
                <div className="text-right text-xs text-gray-500">
                  <div className="font-bold text-gray-700">{entry.consecutiveDays} days away</div>
                  <div>{entry.daysRemaining} days remaining</div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-3 text-xs border-t pt-3">
                <div><div className="text-gray-400">Home depot</div><div className="font-medium">{SITES[entry.homeDepot as keyof typeof SITES]?.label ?? entry.homeDepot}</div></div>
                <div><div className="text-gray-400">Deployment start</div><div className="font-medium">{formatDate(entry.deploymentStart)}</div></div>
                <div><div className="text-gray-400">Deployment end</div><div className="font-medium">{formatDate(entry.deploymentEnd)}</div></div>
              </div>
              {acc && (
                <div className="mt-2 text-xs text-blue-700 bg-blue-50 rounded-lg px-3 py-2 flex items-center gap-2">
                  <Bed className="w-3.5 h-3.5" />
                  Accommodation: {acc.propertyName} · {acc.address}
                  {acc.assignedEmployees.find(e => e.employeeId === entry.employeeId)?.confirmedAt
                    ? <span className="ml-auto text-green-600 flex items-center gap-1"><CheckCircle className="w-3 h-3" />Confirmed</span>
                    : <span className="ml-auto text-amber-600 flex items-center gap-1"><Clock className="w-3 h-3" />Awaiting confirmation</span>
                  }
                </div>
              )}
              {entry.note && <p className="mt-2 text-xs text-gray-500 italic">{entry.note}</p>}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// DISTANCE REFERENCE TAB (unchanged)
// ─────────────────────────────────────────────────────────────────

function DistanceReferenceTab() {
  const matrix = MOCK_DISTANCE_MATRIX
  const rows = [
    { dist: 'Under 50 km',     assign: 'Any duration',             level: 'GREEN',  action: 'No action — normal daily travel' },
    { dist: '50 km – 99 km',   assign: '1–2 days',                 level: 'YELLOW', action: 'PM to assess. Accommodation to be offered for 3+ days.' },
    { dist: '50 km – 99 km',   assign: '3+ consecutive days',      level: 'AMBER',  action: 'Accommodation must be offered. PM to action FM flag.' },
    { dist: '100 km or more',  assign: 'Any duration',             level: 'AMBER',  action: 'Accommodation required for 1+ days. PM to arrange.' },
    { dist: '100 km or more',  assign: '2+ consecutive days',      level: 'RED',    action: 'Roster blocked. Accommodation mandatory. WHSO to be notified.' },
    { dist: 'Any',             assign: '6+ consecutive days',      level: 'RED',    action: 'Mandatory rest day. Roster blocked regardless of distance.' },
  ]
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">FM Risk thresholds for cross-region deployment. Based on Safe Work Australia Code of Practice (September 2025).</p>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase border-b">
            <th className="px-4 py-3 text-left">Distance from home</th>
            <th className="px-4 py-3 text-left">Assignment duration</th>
            <th className="px-4 py-3 text-left">FM Risk</th>
            <th className="px-4 py-3 text-left">Required action</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r, i) => {
              const rm = FM_RISK_META[r.level] ?? FM_RISK_META.GREEN
              return (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{r.dist}</td>
                  <td className="px-4 py-3">{r.assign}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${rm.bg} ${rm.text}`}>{r.level}</span></td>
                  <td className="px-4 py-3 text-gray-600">{r.action}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-xs text-blue-800 space-y-1">
        <p className="font-semibold">Distance reference data — {matrix.length} routes loaded</p>
        {matrix.slice(0, 5).map(d => (
          <p key={d.id}>{d.from} → {d.to}: {d.km}km ({d.mins} min) · <span className={`font-medium ${FM_RISK_META[d.fmStatus]?.text ?? ''}`}>{d.fmStatus}</span></p>
        ))}
        <p className="text-blue-600 italic">+{Math.max(0, matrix.length - 5)} more routes in the distance matrix…</p>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────



// ─────────────────────────────────────────────────────────────────
// PHASE 4 — ACCOMMODATION BOOKING CONFIRMATION LETTER
// Formatted letter / email attachment for the employee confirming
// all accommodation details: property, keyholder, co-occupants,
// check-in/out dates, amenities, and PM contact.
// ─────────────────────────────────────────────────────────────────
function generateAccommodationConfirmation(acc: EnhancedAccommodation, employeeName: string) {
  const now    = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const fmtD   = (d: string) => d ? new Date(d).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '—'
  const pt     = PROPERTY_TYPE_META[acc.propertyType]
  const coOccs = acc.assignedEmployees.filter(e => e.employeeName !== employeeName)
  const amenities = [
    acc.parkingAvailable && '🚗 Parking available on site',
    acc.kitchenAvailable && '🍳 Kitchen / cooking facilities',
    acc.wifiAvailable    && '📶 WiFi available',
    acc.laundryAvailable && '🫧 Laundry facilities',
  ].filter(Boolean)

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Accommodation Confirmation — ${acc.propertyName}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:11pt;color:#111;margin:20mm}
h1{font-size:22pt;font-weight:900;color:#1B3A6B;margin:0}
h2{font-size:11.5pt;color:#1B3A6B;border-bottom:1.5px solid #1B3A6B;padding-bottom:3px;margin:16px 0 8px}
.hdr{border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:18px;display:flex;justify-content:space-between;align-items:flex-end}
p{line-height:1.85;margin-bottom:10px}
.prop-banner{background:#1B3A6B;color:#fff;border-radius:6px;padding:14px 20px;margin:14px 0}
.info table{width:100%;border-collapse:collapse;margin-bottom:12px}
.info td{padding:5px 8px;border-bottom:1px solid #f3f4f6}.info td:first-child{width:36%;font-weight:bold;color:#555;background:#f9fafb}
.keyholder-box{background:#fff7ed;border:1.5px solid #fb923c;border-radius:5px;padding:12px 16px;margin:12px 0}
.coocup-box{background:#eff6ff;border:1.5px solid #60a5fa;border-radius:5px;padding:12px 16px;margin:12px 0}
.amenity-box{background:#f0fdf4;border:1.5px solid #86efac;border-radius:5px;padding:10px 16px;margin:10px 0}
ul{margin:6px 0;padding-left:22px;line-height:1.8}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:22px;border-top:1px solid #eee;padding-top:7px}
@media print{body{margin:14mm}}
</style></head><body>
<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 12 345 678 901 · 14 Commerce Dr, Salisbury QLD 4107 · 07 3344 5500</div></div>
  <div style="text-align:right"><div style="font-size:9pt;color:#888">Issued: ${now}</div></div>
</div>

<p>To: <strong>${employeeName}</strong></p>
<br>
<p><strong>ACCOMMODATION BOOKING CONFIRMATION</strong></p>
<br>
<div class="prop-banner">
  <div style="font-size:10pt;opacity:0.8;margin-bottom:3px">${pt.icon} ${pt.label}</div>
  <div style="font-size:20pt;font-weight:900">${acc.propertyName}</div>
  <div style="font-size:11pt;margin-top:4px;opacity:0.9">${acc.address}</div>
  ${acc.bookingReference ? `<div style="font-size:9.5pt;margin-top:6px;opacity:0.75">Booking reference: ${acc.bookingReference}</div>` : ''}
</div>

<p>Dear ${employeeName},</p>
<p>Please find below your confirmed accommodation details for your upcoming deployment. Keep this letter with you for site access and key collection.</p>

<div class="info"><table>
  <tr><td>Check-in</td><td><strong>${fmtD(acc.checkIn)}</strong></td></tr>
  <tr><td>Check-out</td><td><strong>${fmtD(acc.checkOut)}</strong></td></tr>
  <tr><td>Total nights</td><td>${acc.totalNights} nights</td></tr>
  <tr><td>Property type</td><td>${pt.label}</td></tr>
  <tr><td>Job reference</td><td>${acc.jobNumber ?? '—'}</td></tr>
  ${acc.bookingReference ? `<tr><td>Booking reference</td><td><strong>${acc.bookingReference}</strong> — quote on check-in</td></tr>` : ''}
</table></div>

<div class="keyholder-box">
  <div style="font-size:10pt;font-weight:900;color:#c2410c;margin-bottom:8px">🔑 KEY ACCESS — KEYHOLDER CONTACT</div>
  <table style="width:100%;border-collapse:collapse">
    <tr><td style="width:36%;font-weight:bold;color:#555;padding:4px 0">Keyholder name</td><td><strong>${acc.keyholderName}</strong></td></tr>
    ${acc.keyholderRole ? `<tr><td style="font-weight:bold;color:#555;padding:4px 0">Role</td><td>${acc.keyholderRole}</td></tr>` : ''}
    <tr><td style="font-weight:bold;color:#555;padding:4px 0">Phone</td><td><strong style="font-size:13pt;color:#1B3A6B">${acc.keyholderPhone}</strong> — save this number</td></tr>
    ${acc.keyReturnProcedure ? `<tr><td style="font-weight:bold;color:#555;padding:4px 0">Key return</td><td>${acc.keyReturnProcedure}</td></tr>` : ''}
  </table>
</div>

${coOccs.length > 0 ? `<div class="coocup-box">
  <div style="font-size:10pt;font-weight:900;color:#1d4ed8;margin-bottom:8px">👥 CO-OCCUPANTS — other MCB staff at this property</div>
  <ul>${coOccs.map(e => `<li>${e.employeeName}</li>`).join('')}</ul>
  <p style="font-size:9.5pt;color:#555;margin:8px 0 0">Please coordinate arrival times and key access with your co-occupants via the TrafficProcure app. Phone numbers are not shared — use in-app messaging.</p>
</div>` : ''}

${amenities.length > 0 ? `<div class="amenity-box">
  <div style="font-size:10pt;font-weight:900;color:#166534;margin-bottom:6px">✅ Amenities at this property</div>
  <ul>${amenities.map(a => `<li>${a}</li>`).join('')}</ul>
</div>` : ''}

${acc.notes ? `<h2>Special notes</h2><p>${acc.notes}</p>` : ''}

<h2>Important reminders</h2>
<ul>
  <li>Contact the keyholder before your arrival to arrange key collection — do not arrive unannounced</li>
  <li>Return all keys to the keyholder by check-out date. Failure to return keys may result in a locksmith charge to you</li>
  <li>Leave the property in the condition you found it. Report any maintenance issues to the PM</li>
  <li>Travel allowance claims must be submitted via your timesheet for the period of your stay</li>
  <li>For queries about this booking, contact: <strong>Jane Smith (PM) — 0412 345 678</strong></li>
</ul>

<p style="font-size:10pt;color:#555;margin-top:14px">Booked by: ${acc.bookedByName} · Booking date: ${acc.bookedAt ? new Date(acc.bookedAt).toLocaleDateString('en-AU') : now}</p>

<div class="footer">MCB Civil Electrical Pty Ltd · Accommodation Confirmation · ${acc.propertyName} · ${now} · Confidential — for named recipient only</div>
</body></html>`
  const win = window.open('', '_blank', 'width=960,height=820')
  if (!win) { alert('Pop-up blocked — allow pop-ups to print confirmation'); return }
  win.document.write(html); win.document.close()
  setTimeout(() => { win.focus(); win.print() }, 350)
}
export function Travel() {
  const { isTier, user } = useAuthStore()
  const [flags,         setFlags]         = useState<FMFlag[]>(MOCK_FM_FLAGS as FMFlag[])
  const [enhancedAcc,   setEnhancedAcc]   = useState<EnhancedAccommodation[]>(INITIAL_ENHANCED)
  const [tab,           setTab]           = useState<'accommodation'|'fm_flags'|'away'|'distances'|'expenses'>('accommodation')
  const [bookingFor,    setBookingFor]    = useState<FMFlag | null>(null)
  const [showNewBooking,setShowNewBooking] = useState(false)
  const [accFilter,     setAccFilter]     = useState('ALL')
  const [fmFilter,      setFmFilter]      = useState('ALL')

  const canAct = isTier('B_PC')

  const openFlags  = flags.filter(f => ['OPEN','PENDING_CONF'].includes(f.status))
  const redCount   = openFlags.filter(f => f.riskLevel === 'RED').length
  const amberCount = openFlags.filter(f => f.riskLevel === 'AMBER').length
  const awayCount  = MOCK_AWAY_REGISTER.length
  const activeAccom = enhancedAcc.filter(a => a.status === 'ACTIVE').length

  const handleAcknowledge  = (id: string) =>
    setFlags(prev => prev.map(f => f.id === id ? { ...f, status: 'ACKNOWLEDGED', pmAcknowledgedAt: new Date().toISOString(), pmAcknowledgedBy: user?.fullName ?? 'PM' } : f))

  const handleMarkOwn = (id: string) =>
    setFlags(prev => prev.map(f => f.id === id ? { ...f, status: 'RESOLVED', pmAction: 'EMPLOYEE_OWN_ACCOMMODATION', pmAcknowledgedAt: new Date().toISOString(), pmAcknowledgedBy: user?.fullName ?? 'PM' } : f))

  const handleBookSave = (newAcc: any) => {
    // Old-style booking from FM flag — wrap into legacy format
    setEnhancedAcc(prev => [...prev, {
      id: newAcc.id, propertyName: newAcc.accommodationName, propertyType: 'HOTEL',
      address: newAcc.address, bookingReference: newAcc.bookingReference,
      jobId: newAcc.jobId, jobNumber: newAcc.jobNumber, riskLevel: newAcc.riskLevel,
      checkIn: newAcc.checkIn, checkOut: newAcc.checkOut,
      nightlyRateAud: newAcc.nightlyRateAud, totalNights: newAcc.totalNights,
      totalBeds: 1, keyholderName: 'PM / booking contact', keyholderPhone: 'See notes',
      parkingAvailable: false, kitchenAvailable: false, wifiAvailable: false, laundryAvailable: false,
      assignedEmployees: [{ employeeId: newAcc.employeeId, employeeName: newAcc.employeeName, assignedAt: newAcc.bookedAt, assignedByName: newAcc.bookedByName, notifiedAt: newAcc.bookedAt, confirmedAt: newAcc.employeeConfirmed ? newAcc.employeeConfirmedAt : undefined }],
      status: 'UPCOMING', notes: newAcc.notes, bookedByName: newAcc.bookedByName, bookedAt: newAcc.bookedAt,
    }])
    setFlags(prev => prev.map(f => f.id === bookingFor?.id ? { ...f, status: 'PENDING_CONF', pmAction: 'ACCOMMODATION_ARRANGED', accommodationId: newAcc.id, pmAcknowledgedAt: new Date().toISOString(), pmAcknowledgedBy: user?.fullName ?? 'PM' } : f))
    setBookingFor(null)
  }

  const handleAssign = (acc: EnhancedAccommodation, empId: string) => {
    const emp = MOCK_EMPLOYEES.find(e => e.id === empId)!
    const now = new Date().toISOString()
    setEnhancedAcc(prev => prev.map(a => a.id !== acc.id ? a : {
      ...a,
      assignedEmployees: [...a.assignedEmployees, {
        employeeId: empId, employeeName: emp.fullName,
        assignedAt: now, assignedByName: user?.fullName ?? 'PM', notifiedAt: now,
      }],
    }))
  }

  // Rollback audit log
  const [rollbackLog, setRollbackLog] = useState<{
    employeeId: string; employeeName: string; accId: string; propertyName: string
    rolledBackAt: string; rolledBackByName: string; reasonLabel: string; notes: string | null
    originalCheckIn: string; originalCheckOut: string; jobNumber?: string
  }[]>([])

  const handleUnassign = (acc: EnhancedAccommodation, empId: string, reason = 'OTHER', reasonLabel = 'Other', notes: string | null = null) => {
    const ae = acc.assignedEmployees.find(e => e.employeeId === empId)
    // Remove from accommodation
    setEnhancedAcc(prev => prev.map(a => a.id !== acc.id ? a : {
      ...a, assignedEmployees: a.assignedEmployees.filter(e => e.employeeId !== empId),
    }))
    // Log the rollback for audit trail
    if (ae) {
      setRollbackLog(prev => [{
        employeeId: empId,
        employeeName: ae.employeeName,
        accId: acc.id,
        propertyName: acc.propertyName,
        rolledBackAt: new Date().toISOString(),
        rolledBackByName: user?.fullName ?? 'PM',
        reasonLabel,
        notes,
        originalCheckIn: acc.checkIn,
        originalCheckOut: acc.checkOut,
        jobNumber: acc.jobNumber,
      }, ...prev])
    }
  }

  const handleResend = (acc: EnhancedAccommodation, empId: string) => {
    const now = new Date().toISOString()
    setEnhancedAcc(prev => prev.map(a => a.id !== acc.id ? a : {
      ...a, assignedEmployees: a.assignedEmployees.map(e => e.employeeId !== empId ? e : { ...e, notifiedAt: now }),
    }))
  }

  const filteredAcc   = accFilter === 'ALL' ? enhancedAcc : enhancedAcc.filter(a => a.status === accFilter)
  const filteredFlags = fmFilter  === 'ALL' ? flags       : flags.filter(f => f.riskLevel === fmFilter)

  return (
    <div className="space-y-5">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Travel &amp; Accommodation</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Fatigue management · accommodation register · away register
          {(redCount + amberCount) > 0 && (
            <span className="ml-2 text-red-600 font-bold">
              · {redCount + amberCount} FM flag{redCount + amberCount !== 1 ? 's' : ''} require action
            </span>
          )}
        </p>
      </div>

      {/* WHS notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 flex items-start gap-3">
        <Shield className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800">
          <strong>WHS obligation (Safe Work Australia Code of Practice, Sep 2025):</strong>{' '}
          Accommodation must be arranged for any worker deployed over 100 km from home for 2 or more consecutive days,
          or over 50 km from home for 3 or more consecutive days.
        </p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Active accommodation',      value: activeAccom,                      bg: 'bg-blue-50',    text: 'text-blue-700',   icon: Bed },
          { label: 'FM flags requiring action', value: redCount + amberCount,            bg: redCount > 0 ? 'bg-red-50' : 'bg-amber-50', text: redCount > 0 ? 'text-red-700' : 'text-amber-700', icon: AlertTriangle },
          { label: 'Employees away from depot', value: awayCount,                        bg: 'bg-purple-50',  text: 'text-purple-700', icon: MapPin },
          { label: 'Yellow flags',              value: flags.filter(f => f.riskLevel === 'YELLOW').length, bg: 'bg-yellow-50', text: 'text-yellow-700', icon: Activity },
        ].map(c => (
          <div key={c.label} className={`rounded-xl border border-gray-200 p-4 ${c.bg}`}>
            <div className={`text-3xl font-bold ${c.text}`}>{c.value}</div>
            <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
              <c.icon className="w-3.5 h-3.5" />{c.label}
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit flex-wrap">
        {([
          { key: 'accommodation', label: `Accommodation (${enhancedAcc.length})` },
          { key: 'fm_flags',      label: `FM flags${(redCount + amberCount) > 0 ? ` \u26A0${redCount + amberCount}` : ` (${flags.length})`}` },
          { key: 'away',          label: `Away register (${MOCK_AWAY_REGISTER.length})` },
          { key: 'distances',     label: 'Distance reference' },
          { key: 'expenses',      label: 'Expense claims' },
        ] as const).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors ${
              tab === t.key ? 'bg-white text-gray-900 shadow-sm'
                : `text-gray-500 hover:text-gray-700 ${t.key === 'fm_flags' && (redCount + amberCount) > 0 && tab !== 'fm_flags' ? 'text-red-600' : ''}`
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Accommodation tab ─────────────────────────────────── */}
      {tab === 'accommodation' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex gap-1.5 flex-wrap">
              {['ALL','ACTIVE','UPCOMING','COMPLETED'].map(f => (
                <button key={f} onClick={() => setAccFilter(f)}
                  className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${
                    accFilter === f ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}>
                  {f === 'ALL' ? 'All' : f.charAt(0) + f.slice(1).toLowerCase()}
                </button>
              ))}
            </div>
            {canAct && (
              <button onClick={() => setShowNewBooking(true)}
                className="btn-primary flex items-center gap-2 text-sm">
                <Plus className="w-4 h-4" /> New booking
              </button>
            )}
          </div>
          {filteredAcc.length === 0
            ? (
              <div className="bg-white border border-gray-200 rounded-xl py-14 text-center text-gray-400">
                <Bed className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No accommodation records</p>
                {canAct && <button onClick={() => setShowNewBooking(true)} className="mt-3 btn-primary text-sm">Create first booking</button>}
              </div>
            )
            : (
              <div className="space-y-3">
                {filteredAcc.map(a => (
                  <EnhancedAccommodationCard
                    key={a.id} acc={a} allAcc={enhancedAcc}
                    onAssign={handleAssign} onUnassign={handleUnassign}
                    onResend={handleResend} canAct={canAct}
                  />
                ))}
              </div>
            )
          }
        </div>
      )}

      {/* ── FM Flags tab ──────────────────────────────────────── */}
      {tab === 'fm_flags' && (
        <div className="space-y-4">
          {(redCount + amberCount) > 0 && (
            <div className="bg-red-600 text-white rounded-xl px-5 py-3 flex items-center gap-3">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span className="font-semibold">
                {redCount > 0 && `${redCount} RED flag${redCount !== 1 ? 's' : ''} — roster blocked until resolved.`}
                {amberCount > 0 && ` ${amberCount} AMBER flag${amberCount !== 1 ? 's' : ''} — accommodation must be offered.`}
              </span>
            </div>
          )}
          <div className="flex gap-1.5 flex-wrap">
            {['ALL','RED','AMBER','YELLOW'].map(f => {
              const rm = f === 'ALL' ? null : FM_RISK_META[f]
              return (
                <button key={f} onClick={() => setFmFilter(f)}
                  className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${
                    fmFilter === f ? (rm ? `${rm.bg} ${rm.text} border border-current` : 'bg-indigo-600 text-white') : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}>
                  {f === 'ALL' ? `All flags (${flags.length})` : `${f} (${flags.filter(fl => fl.riskLevel === f).length})`}
                </button>
              )
            })}
          </div>
          <div className="space-y-3">
            {[...filteredFlags]
              .sort((a,b) => ['RED','AMBER','YELLOW','GREEN'].indexOf(a.riskLevel) - ['RED','AMBER','YELLOW','GREEN'].indexOf(b.riskLevel))
              .map(flag => (
                <FMFlagCard key={flag.id} flag={flag as any}
                  onAcknowledge={handleAcknowledge}
                  onBook={f => setBookingFor(f as any)}
                  onMarkEmployeeOwn={handleMarkOwn}
                  canAct={canAct} />
              ))
            }
          </div>
        </div>
      )}

      {/* Away register */}
      {tab === 'away' && <AwayRegisterTab />}

      {/* Distance reference */}
      {tab === 'distances' && <DistanceReferenceTab />}

      {/* Expense claims */}
      {tab === 'expenses' && (
        <ExpensesTab
          canApprove={canAct}
          currentUserName={user?.fullName ?? 'User'}
          currentUserId={user?.id ?? 'u1'}
        />
      )}

      {/* ── Modals ─────────────────────────────────────────────── */}
      {showNewBooking && (
        <NewBookingModal
          onClose={() => setShowNewBooking(false)}
          onSave={acc => {
            setEnhancedAcc(prev => [acc, ...prev])
            setShowNewBooking(false)
          }}
        />
      )}

      {bookingFor && (
        <BookAccommodationModal
          flag={bookingFor as any}
          onClose={() => setBookingFor(null)}
          onBook={handleBookSave}
        />
      )}
    </div>
  )
}
